/**
 * Optimized-image substitute for SPA (plain <img>).
 */
import type { CSSProperties, ImgHTMLAttributes, MouseEventHandler } from "react";

/** What a bundler hands back when an image module is imported. */
type StaticImageLike = { src?: string; default?: string };

export interface AppImageProps
  extends Omit<
    ImgHTMLAttributes<HTMLImageElement>,
    "src" | "width" | "height" | "placeholder"
  > {
  /** Either a URL string or an imported image module. */
  src?: string | StaticImageLike | null;
  alt?: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
  /** Load eagerly rather than lazily. */
  priority?: boolean;
  /** Stretch to fill the nearest positioned ancestor. */
  fill?: boolean;
  objectFit?: CSSProperties["objectFit"];
  onClick?: MouseEventHandler<HTMLImageElement>;
  /* Accepted for `next/image` call-site compatibility; deliberately unused. */
  sizes?: string;
  quality?: number | string;
  placeholder?: string;
  blurDataURL?: string;
}

export default function Image({
  src,
  alt,
  width,
  height,
  className,
  style,
  priority,
  fill,
  sizes: _sizes,
  quality: _quality,
  placeholder: _placeholder,
  blurDataURL: _blur,
  objectFit,
  onClick,
  ...rest
}: AppImageProps) {
  const resolvedSrc =
    typeof src === "string" ? src : src?.src ?? src?.default ?? "";

  const imgStyle: CSSProperties = { ...(style || {}) };
  if (fill) {
    Object.assign(imgStyle, {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: objectFit || "cover",
    } satisfies CSSProperties);
  }

  return (
    <img
      src={resolvedSrc || undefined}
      alt={alt ?? ""}
      width={fill ? undefined : width}
      height={fill ? undefined : height}
      className={className}
      style={imgStyle}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      onClick={onClick}
      {...rest}
    />
  );
}
